/**
 * unprotected-canvas.js — Unprotected workload management + workload chip builder
 *
 * Owns:
 *  - addUnprotectedWorkloadToSite, removeUnprotectedWorkloadFromSite,
 *    updateUnprotectedWorkloadField
 *  - buildGfChipMarkup + GF_CHIP_VARIANT_CONFIG (workload/protected/uncategorised chip variants)
 *  - GF_SVG_SERVER (window-bridged for step2-canvas.js)
 *  - removeProtectedWorkloadFromSite, removeUncategorisedWorkloadFromSite
 *
 * Extracted from §UNPROTECTED-CANVAS in script.js (was lines 3441–3595).
 * Dead code removed: GF_SVG_LINK_BROKEN (defined but never used).
 * refreshGfSiteChips calls replaced with emit(BUS_EVENTS.SITE_LIST_CHANGED) for
 * removeProtected/Uncategorised — a full re-render instead of partial update, which
 * is correct and avoids the circular dependency on §VAULT-DRAWER.
 */

import { emit, BUS_EVENTS } from './bus.js';
import { scheduleRenderEstimate, getGfChipIcon } from './recalc.js';
import { syncMatrixToHiddenInputs, step2MatrixState, isMatrixCellNA, renderScopeMatrix } from './scope-matrix.js';
import {
    getSiteById,
    getGfWorkloadLabel,
    getGfWorkloadUnit,
    getWorkloadTileDisplayLabel,
    normalizeWorkloadCustomLabel,
} from './helpers.js';
import { escapeHtml, isProtectedTbWorkload } from './utils.js';

// ── SVG constants ─────────────────────────────────────────────────────────────

/** Server icon — also used by step2-canvas.js via window.GF_SVG_SERVER */
export const GF_SVG_SERVER = `<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect x="2" y="3" width="20" height="6" rx="1"/><rect x="2" y="15" width="20" height="6" rx="1"/><line x1="6" y1="6" x2="6.01" y2="6"/><line x1="6" y1="18" x2="6.01" y2="18"/></svg>`;
// Window bridge: step2-canvas.js accesses this as window.GF_SVG_SERVER
window.GF_SVG_SERVER = GF_SVG_SERVER;

// ── Chip markup ───────────────────────────────────────────────────────────────

/**
 * Per-variant data-attribute config for buildGfChipMarkup.
 *  chipAttr   — attribute on the root <span> identifying this chip
 *  removeAttr — attribute on the remove <button>
 *  qtyAttr    — attribute on the qty <input>
 *  gripTitle  — tooltip on the drag handle
 */
export const GF_CHIP_VARIANT_CONFIG = {
    wl:            { chipAttr: 'data-gf-chip',     removeAttr: 'data-gf-chip-remove', qtyAttr: 'data-gf-chip-qty', gripTitle: 'Drag to reorder' },
    protected:     { chipAttr: 'data-gf-pw-chip',  removeAttr: 'data-gf-pw-remove',   qtyAttr: 'data-gf-pw-qty',   gripTitle: 'Drag to reorder' },
    uncategorised: { chipAttr: 'data-gf-unc-chip', removeAttr: 'data-gf-unc-remove',  qtyAttr: 'data-gf-unc-qty',  gripTitle: 'Drag to correct group' },
};

/**
 * Build a canvas chip for any workload variant ('wl', 'protected', or 'uncategorised').
 * All three variants share identical markup — only the CSS modifier class, drag-handle
 * tooltip, and data-attribute names differ (captured in GF_CHIP_VARIANT_CONFIG).
 * SVG icons (GF_SVG_GRIP_SM, GF_SVG_PENCIL, GF_SVG_CLOSE) are read from window globals
 * set by §GF-CHIP-LAYOUT in script.js.
 */
export function buildGfChipMarkup(workload, chipType = 'wl') {
    const { chipAttr, removeAttr, qtyAttr, gripTitle } = GF_CHIP_VARIANT_CONFIG[chipType];
    const iconSvg = getGfChipIcon(workload.value);
    const label = getGfWorkloadLabel(workload.value);
    const unit = getGfWorkloadUnit(workload.value);
    const displayLabel = getWorkloadTileDisplayLabel(label, unit);
    const isUnstructured = isProtectedTbWorkload(workload.value);
    const qty = isUnstructured
        ? (typeof workload.protectedTb === 'number' ? workload.protectedTb : 0)
        : (workload.count || 0);
    const qtyEmpty = qty === 0 ? ' is-empty' : '';
    const step = isUnstructured ? '0.1' : '1';

    return `<span class="gf-chip gf-chip--${chipType}" ${chipAttr}="${workload.rowId}" data-gf-chip-value="${escapeHtml(workload.value)}">
        <span class="gf-chip-row-name">
            <span class="gf-chip-grip" title="${gripTitle}">${window.GF_SVG_GRIP_SM}</span>
            <span class="gf-chip-icon" aria-hidden="true">${iconSvg}</span>
            <span class="gf-chip-label" contenteditable="true" spellcheck="false"
                  data-gf-chip-inline-label="${workload.rowId}"
                  data-chip-default-label="${escapeHtml(displayLabel)}"
                  title="${escapeHtml(label)}"
            >${escapeHtml(workload.customLabel || displayLabel)}</span><span class="gf-chip-label-pencil" aria-hidden="true">${window.GF_SVG_PENCIL}</span>
            <button type="button" class="gf-chip-remove" ${removeAttr}="${workload.rowId}" aria-label="Remove ${escapeHtml(label)}">${window.GF_SVG_CLOSE}</button>
        </span>
        <span class="gf-chip-row-qty">
            <span class="gf-chip-x-symbol" aria-hidden="true">×</span>
            <input class="gf-chip-qty${qtyEmpty}"
                   type="number"
                   min="0"
                   style="width: ${Math.max(1, qty > 0 ? qty.toString().length : 1)}ch"
                   step="${step}"
                   value="${qty > 0 ? qty : ''}"
                   placeholder="0"
                   ${qtyAttr}="${workload.rowId}"
                   aria-label="Quantity for ${escapeHtml(label)}">
            <span class="gf-chip-unit">${escapeHtml(unit)}</span>
        </span>
    </span>`;
}

// ── Unprotected workload management ──────────────────────────────────────────

export function addUnprotectedWorkloadToSite(siteId, workloadType = 'vmware', count = 50) {
    const site = getSiteById(siteId);
    if (!site) return;
    if (!Array.isArray(site.unprotectedWorkloads)) site.unprotectedWorkloads = [];
    site.unprotectedWorkloads.push(window.buildDefaultUnprotectedWorkloadEntry(workloadType, count));
    emit(BUS_EVENTS.SITE_LIST_CHANGED);
    emit(BUS_EVENTS.DESIGN_SYNC_NEEDED);
    renderScopeMatrix();
    syncMatrixToHiddenInputs();
    emit(BUS_EVENTS.COUNTS_SYNC_NEEDED);
    emit(BUS_EVENTS.VALIDATION_SYNC_NEEDED);
    scheduleRenderEstimate();
}

export function removeUnprotectedWorkloadFromSite(siteId, workloadId) {
    const site = getSiteById(siteId);
    if (!site || !Array.isArray(site.unprotectedWorkloads)) return;
    site.unprotectedWorkloads = site.unprotectedWorkloads.filter((w) => w.id !== workloadId);
    emit(BUS_EVENTS.SITE_LIST_CHANGED);
    emit(BUS_EVENTS.DESIGN_SYNC_NEEDED);
    renderScopeMatrix();
    syncMatrixToHiddenInputs();
    emit(BUS_EVENTS.COUNTS_SYNC_NEEDED);
    emit(BUS_EVENTS.VALIDATION_SYNC_NEEDED);
    scheduleRenderEstimate();
}

export function updateUnprotectedWorkloadField(siteId, workloadId, field, value) {
    const site = getSiteById(siteId);
    if (!site || !Array.isArray(site.unprotectedWorkloads)) return;
    const workload = site.unprotectedWorkloads.find((w) => w.id === workloadId);
    if (!workload) return;
    if (field === 'workloadType') {
        workload.workloadType = value;
        // When workload type changes, clear state for treatments now N/A
        const key = String(workloadId);
        if (step2MatrixState.unmanaged[key]) {
            Object.keys(step2MatrixState.unmanaged[key]).forEach((tKey) => {
                if (isMatrixCellNA(tKey, value)) step2MatrixState.unmanaged[key][tKey] = false;
            });
        }
    } else if (field === 'count') {
        workload.count = Math.max(0, parseInt(value, 10) || 0);
    } else if (field === 'customLabel') {
        const trimmed = normalizeWorkloadCustomLabel(value);
        if (trimmed) { workload.customLabel = trimmed; } else { delete workload.customLabel; }
        renderScopeMatrix();
        return; // label change doesn't affect counts or estimates
    }
    renderScopeMatrix();
    syncMatrixToHiddenInputs();
    emit(BUS_EVENTS.COUNTS_SYNC_NEEDED);
    emit(BUS_EVENTS.VALIDATION_SYNC_NEEDED);
    scheduleRenderEstimate();
}

export function removeProtectedWorkloadFromSite(siteId, rowId) {
    const site = getSiteById(siteId);
    if (!site || !Array.isArray(site.protectedWorkloads)) return;
    site.protectedWorkloads = site.protectedWorkloads.filter((w) => w.rowId !== rowId);
    // Full re-render (replaces refreshGfSiteChips partial-update from §VAULT-DRAWER)
    emit(BUS_EVENTS.SITE_LIST_CHANGED);
}

export function removeUncategorisedWorkloadFromSite(siteId, rowId) {
    const site = getSiteById(siteId);
    if (!site || !Array.isArray(site.uncategorisedWorkloads)) return;
    site.uncategorisedWorkloads = site.uncategorisedWorkloads.filter((w) => w.rowId !== rowId);
    // Full re-render (replaces refreshGfSiteChips partial-update from §VAULT-DRAWER)
    emit(BUS_EVENTS.SITE_LIST_CHANGED);
}
